<script setup>
/* eslint-disable */
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { debounce } from "lodash";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import ProjectsService from "@/api/services/projects.service";
import ClientServices from "@/api/services/clients";
import { useUiStore } from "@/app/stores/uiStore";
import notification from '@/utils/plugins/NotificationService'
import router from "@/app/router/router";
import SharedButtonPlus from "@/components/shared/SharedButtonPlus.vue";
import SharedDeleteConfirmDialog from "@/components/shared/SharedDeleteConfirmDialog.vue";
import Notification from "@/utils/plugins/NotificationService";
import errorHandler from "@/utils/plugins/ErrorHandler";
import { useRoute } from "vue-router";


const route = useRoute();
const uiStore = useUiStore();
const clientsRestoreFiltersStorageKey = 'clients-restore-filters';
const isCreateMode = computed(() => route.name === 'create-client');
const extranetUsers = ref([]);
const clientSubmitAttempted = ref(false);
const user = ref({
  name: '',
  lastName: '',
  email: '',
  roles: null
})
const resetUser = () => {
  user.value = {
    name: '',
    lastName: '',
    email: '',
    roles: optionsRoles.value.find(r => r.roleName === 'ROLE_CUSTOMER') ?? null
  }
}
const getEmptyClient = () => ({
  phone: '',
  contactPersonName: '',
  name: '',
  email: '',
  tag: '',
  cif: '',
  address: '',
  extranetUrl: '',
  source: 'connectus',
  internalCustomer: false,
  holdedName: null,
  holdedId: null,
})
const client = ref(getEmptyClient())
const tagManuallyEdited = ref(false);
const isSuggestingTag = ref(false);
const optionsClient = ref([]);
const optionsProject = ref([]);
const optionsRoles = ref([
  { roleName: 'ROLE_CUSTOMER', name: 'Usuario' },
  { roleName: 'ROLE_CUSTOMER_ADMIN', name: 'Administrador' },
])
const optionsSource = [
  { label: 'Connectus', value: 'connectus' },
  { label: 'Clockify', value: 'clockify' },
];
const pageSizeOptions = [5, 10, 20]
const projectFilters = ref([{
  name: 'No Archivados',
  value: 'notArchived',
  active: true,
}, {
  name: 'Archivados',
  value: 'archived',
  active: false,
}, {
  name: 'Todos',
  value: 'all',
  active: false,
}])
const selectedProjectFilter = ref('notArchived')
const isSourceDisabled = computed(() => !isCreateMode.value && client.value?.source === 'clockify')
const dialogVisible = ref(false);
const deactivateUserDialogShown = ref(false);
const selectedUserToDeactivate = ref(null);
const isDeactivatingUser = ref(false);
const extranetUsersPage = ref(1)
const extranetUsersItemsPerPage = ref(5)
const projectsPage = ref(1)
const projectsItemsPerPage = ref(5)
const clientProjects = computed(() => {
  if (selectedProjectFilter.value === 'notArchived') {
    return client.value?.projectsNotArchived || [];
  }

  const projects = client.value?.projects || [];
  if (selectedProjectFilter.value === 'archived') {
    return projects.filter((project) => project.archived === true);
  }

  return projects;
})
const paginatedExtranetUsers = computed(() => {
  const start = (extranetUsersPage.value - 1) * extranetUsersItemsPerPage.value
  return extranetUsers.value.slice(start, start + extranetUsersItemsPerPage.value)
})
const paginatedClientProjects = computed(() => {
  const start = (projectsPage.value - 1) * projectsItemsPerPage.value
  return clientProjects.value.slice(start, start + projectsItemsPerPage.value)
})
const getClients = async () => {
  const response = await ProjectsService.getClients();
  optionsClient.value = response?.data?.["hydra:member"] ?? [];
}
const getProjects = async () => {
  let params = {
    archived: false,
    itemsPerPage: 99999,
    page: 1,
    'order[initialDate]': 'DESC'
  }
  const response = await ProjectsService.getProjects(params);
  optionsProject.value = response?.data?.["hydra:member"] ?? [];

}
// Devuelve false si el cliente no se pudo cargar (borrado, 403, sesión caducada...)
// para que loadClientPage() aborte en vez de crashear con datos a medio cargar.
const getClient = async () => {
  const id = route.params.id;
  try {
    const response = await ClientServices.getClient(id);
    client.value = {
      ...getEmptyClient(),
      ...response.data,
      internalCustomer: response.data?.internalCustomer ?? false,
    };
    return true;
  } catch (e) {
    const status = e?.response?.status;
    if (status === 404) {
      Notification.addNotification('Este cliente ya no existe. Puede que haya sido eliminado.', 'warning', 6000);
    } else {
      // 401 (sesión caducada), 403, 500... el errorHandler global ya sabe qué
      // mensaje mostrar y, si aplica, hace logout y redirige a login por su cuenta.
      await errorHandler(e, null);
    }
    if (status !== 401) {
      router.push({ name: 'clients' });
    }
    return false;
  }
}
const loadExtranetUsers = async () => {
  const id = route.params.id;
  const response = await ClientServices.getExtranetUsers(id);
  extranetUsers.value = (response?.data?.["hydra:member"] ?? []).filter(v => v.active === true);
  extranetUsersPage.value = 1;

  extranetUsers.value.forEach(us => {
    us.roles = optionsRoles.value.filter(v => us.roles.includes(v.roleName));
  })



}
const loadClientPage = async () => {
  uiStore.startLoading();
  try {
    extranetUsersPage.value = 1;
    projectsPage.value = 1;
    tagManuallyEdited.value = false;

    if (isCreateMode.value) {
      client.value = getEmptyClient();
      clientSubmitAttempted.value = false;
      extranetUsers.value = [];
      selectedProjectFilter.value = 'notArchived';
      setActiveProjectFilter('notArchived');
      dialogVisible.value = false;
      await Promise.all([getClients(), getProjects()]);
    } else {
      await Promise.all([getClients(), getProjects()]);
      const clientLoaded = await getClient();
      if (!clientLoaded) return;

      await loadExtranetUsers();
      // El tag ya viene de BD: cualquier cambio posterior de nombre no debe pisarlo automáticamente.
      tagManuallyEdited.value = true;
    }
  } finally {
    uiStore.stopLoading();
  }
}

onMounted(async () => {
  await loadClientPage();
})

onBeforeUnmount(() => {
  debouncedSuggestTag.cancel();
})

watch(() => route.fullPath, async () => {
  await loadClientPage();
})

const applySuggestedTag = async () => {
  const name = client.value.name?.trim();
  if (!name) return;

  isSuggestingTag.value = true;
  try {
    const excludeClientId = isCreateMode.value ? undefined : route.params.id;
    const response = await ClientServices.suggestTag(name, excludeClientId);
    if (response?.data?.tag) {
      client.value.tag = response.data.tag;
    }
  } finally {
    isSuggestingTag.value = false;
  }
}

const debouncedSuggestTag = debounce(applySuggestedTag, 500);

watch(() => client.value.name, () => {
  if (tagManuallyEdited.value) return;
  debouncedSuggestTag();
})

const onTagInput = () => {
  tagManuallyEdited.value = true;
}

const regenerateTag = async () => {
  debouncedSuggestTag.cancel();
  tagManuallyEdited.value = false;
  await applySuggestedTag();
}
const onSave = async () => {
  clientSubmitAttempted.value = true;
  if (!validateClient()) return;

  uiStore.startLoading();
  const id = route.params.id;
  const clientPayload = JSON.parse(JSON.stringify(client.value));
  delete clientPayload.projects;
  delete clientPayload.projectsNotArchived;
  if (clientPayload && !isEmptyClientField(clientPayload.phone)) {
    clientPayload.phone = clientPayload.phone.toString();
  } else {
    delete clientPayload.phone;
  }
  if (!clientPayload.contactPersonName) delete clientPayload.contactPersonName;
  delete clientPayload.extranetUsers;

  try {
    if (isCreateMode.value) {
      const response = await ClientServices.createClient(clientPayload);
      const createdClientId = getClientIdFromResponse(response);

      if (createdClientId) {
        notification.addNotification("Cliente creado correctamente", "success", 10000);
        router.push({ name: 'show-client', params: { id: createdClientId } });
        return;
      }

      Notification.addNotification('No se pudo crear el cliente. Revisa los datos e inténtalo de nuevo.', 'warning', 6000);
      return;
    }

    await ClientServices.editClient(id, clientPayload);
    notification.addNotification("Datos guardados correctamente", "success", 10000);
  } catch (e) {
    Notification.addNotification('No se pudo completar el proceso, ocurrió un error', 'warning', 6000);
  } finally {
    uiStore.stopLoading();
  }
}

const validateClient = () => {
  const requiredFields = [
    client.value.name,
    client.value.cif,
    client.value.email,
    client.value.tag,
  ];

  if (requiredFields.some(isEmptyClientField)) {
    Notification.addNotification('Nombre, CIF, email y etiqueta son obligatorios', 'warning', 6000);
    return false;
  }

  return true;
}

const isEmptyClientField = (field) => !String(field ?? '').trim()

const showRequiredClientField = (field) => clientSubmitAttempted.value && isEmptyClientField(field)

const getClientIdFromResponse = (response) => {
  if (response?.data?.id) return response.data.id;
  if (!response?.data?.['@id']) return null;

  const split = response.data['@id'].split('/');
  return split[split.length - 1];
}
const openDialog = () => {
  resetUser();
  dialogVisible.value = true;
}

const addProjectToClient = () => {
  router.push({
    name: 'create-project',
    query: {
      clientId: client.value.id || route.params.id
    }
  });
}

const desactiveUsuario = async (userToDelete) => {
  selectedUserToDeactivate.value = userToDelete;
  deactivateUserDialogShown.value = true;
}

const resetDeactivateUser = () => {
  selectedUserToDeactivate.value = null;
}

const confirmDeactivateUser = async () => {
  if (!selectedUserToDeactivate.value) return;

  isDeactivatingUser.value = true;
  try {
    const id = route.params.id;
    const extUser = JSON.parse(JSON.stringify(selectedUserToDeactivate.value));
    extUser.client = `/api/clients/${id}`;
    extUser.active = false;
    delete extUser.roles;
    await ClientServices.updateUserExtranet(extUser.id, extUser);
    await loadExtranetUsers();
    notification.addNotification("Usuario dado de baja correctamente", "success", 10000)
    deactivateUserDialogShown.value = false;
    resetDeactivateUser();
  } finally {
    isDeactivatingUser.value = false;
  }
}

const validateUser = () => {
  const requiredFields = [
    user.value.name,
    user.value.lastName,
    user.value.email,
  ];

  if (requiredFields.some(v => !v)) {
    Notification.addNotification('Nombre, apellido y email son obligatorios', 'warning', 6000);
    return false;
  }

  if (!user.value.roles) {
    Notification.addNotification('Debe seleccionar un rol', 'warning', 6000);
    return false;
  }

  return true;
}

const saveUser = async () => {
  if (!validateUser()) return;

  uiStore.startLoading();
  try {
    const id = route.params.id;
    const extUser = JSON.parse(JSON.stringify(user.value));
    extUser.client = `/api/clients/${id}`;
    extUser.roles = [user.value.roles.roleName]
    if (!extUser.id) {
      await ClientServices.createUserExtranet(extUser);
    } else {
      await ClientServices.updateUserExtranet(extUser.id, extUser);
    }
    await loadExtranetUsers();
    resetUser();
    dialogVisible.value = false;
    notification.addNotification("Usuario guardado correctamente", "success", 10000);
  } catch (e) {
    Notification.addNotification('No se pudo guardar el usuario, revisa los datos e inténtalo de nuevo', 'warning', 6000);
  } finally {
    uiStore.stopLoading();
  }
}
const showRoles = (roles) => {
  const rolesShow = JSON.parse(JSON.stringify(roles));
  return rolesShow.map(v => {
    if (v.roleName === "ROLE_CUSTOMER_ADMIN") return "ADMINISTRADOR";
    if (v.roleName === "ROLE_CUSTOMER") return "USUARIO";
  }).join(',')
}
const setActiveProjectFilter = (value) => {
  projectFilters.value.forEach((filter) => {
    filter.active = filter.value === value;
  });
}
const changeProjectFilter = (value) => {
  selectedProjectFilter.value = value;
  setActiveProjectFilter(value);
  projectsPage.value = 1;
}
const formatArchived = (value) => value ? 'Archivado' : 'No archivado'
const getProjectId = (project) => {
  if (project?.id) return project.id;
  if (!project?.['@id']) return null;

  const split = project['@id'].split('/');
  return split[split.length - 1];
}
const goToProject = (project) => {
  const projectId = getProjectId(project);
  if (!projectId) return;

  router.push({ name: 'show-projects', params: { id: projectId } });
}
const onChangeExtranetUsersItemsPerPage = (value) => {
  extranetUsersItemsPerPage.value = value;
  extranetUsersPage.value = 1;
}
const onChangeProjectsItemsPerPage = (value) => {
  projectsItemsPerPage.value = value;
  projectsPage.value = 1;
}
const editUser = (userToEdit) => {
  const selectedRole = userToEdit.roles.find(r => r.roleName === 'ROLE_CUSTOMER_ADMIN')
    ?? userToEdit.roles.find(r => r.roleName === 'ROLE_CUSTOMER')
    ?? null;
  user.value = {
    ...userToEdit,
    roles: selectedRole,
  };

  dialogVisible.value = true;
}

const goBackToClients = () => {
  sessionStorage.setItem(clientsRestoreFiltersStorageKey, 'true');
  router.push({ name: 'clients' });
}
</script>

<template>
  <div>
    <HeaderTitle title="Producción" />
    <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center my-2">
      <div class="client-page-title-row d-flex flex-row justify-content-start align-items-center">
        <button v-if="!isCreateMode" type="button" class="return-btn btn btn-grey client-page-return-btn"
          style="padding-left: 0px; display:initial;" @click="goBackToClients()">
          <font-awesome-icon icon="angle-left"></font-awesome-icon>
          Volver
        </button>
        <svg id="icon-search" class="search-icon client-page-title-icon" width="44" height="37" viewBox="0 0 44 37"
          fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1.15269 30.0573H20.8415V34.6802H11.5851C10.8865 34.6802 10.4324 35.1448 10.4324 35.8301C10.4324 36.527 10.8981 36.98 11.5851 36.98H32.4266C33.1252 36.98 33.5793 36.5154 33.5793 35.8301C33.5793 35.1332 33.1135 34.6802 32.4266 34.6802H23.1585V30.0573H42.8473C43.5459 30.0573 44 29.5927 44 28.9074V1.16994C44 0.473018 43.5343 0.0200195 42.8473 0.0200195H1.15269C0.465732 0.0200195 0 0.484633 0 1.16994V28.8958C0 29.5927 0.465732 30.0573 1.15269 30.0573ZM2.31702 2.33147H41.683V27.7458H2.31702V2.33147Z"
            fill="#474CE6" />
          <path
            d="M38.2133 4.64294H5.78672C5.08812 4.64294 4.63403 5.10756 4.63403 5.79286V24.2729C4.63403 24.9698 5.09977 25.4228 5.78672 25.4228H38.2133C38.9119 25.4228 39.366 24.9582 39.366 24.2729V5.79286C39.366 5.10756 38.9119 4.64294 38.2133 4.64294ZM37.049 23.123H6.95105V6.9544H37.0606V23.123H37.049Z"
            fill="#474CE6" />
        </svg>

        <div class="title-text client-page-title ml-4">
          {{ isCreateMode ? 'Nuevo cliente' : 'Clientes' }}
        </div>
      </div>
    </div>
    <el-row>
      <el-card class="card-search" body-style="padding:0">
        <div class="row">
          <div class="col-md-4 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row w-100">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="client-holded-name" class="">Nombre fiscal (Holded)</label>
                <el-input id="client-holded-name" v-model="client.holdedName" class="search-input" :color="'light'"
                  :placeholder="isCreateMode ? 'Ingrese el nombre fiscal' : 'Sin vincular a Holded'"
                  :readonly="!isCreateMode" :disabled="false">
                  <template v-if="client.holdedName && !isCreateMode" #suffix>
                    <el-tooltip content="Nombre fiscal registrado en Holded" placement="top">
                      <svg style="width:14px;color:#67c23a;" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </el-tooltip>
                  </template>
                </el-input>
              </div>
            </div>
          </div>
          <div class="col-md-4 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="client-name" class="">Nombre</label>
                <el-input id="client-name" v-model="client.name" class="search-input" :rules="{ required: true }"
                  :color="'light'" :placeholder="'Ingrese el nombre'" />
                <p v-if="showRequiredClientField(client.name)" class="invalid-feedback mb-0">
                  <small>Campo obligatorio</small>
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-4 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="client-cif" class="">CIF</label>
                <el-input id="client-cif" v-model="client.cif" class="search-input" :rules="{ required: true }"
                  :color="'light'" :placeholder="'Ingrese el CIF'" />
                <p v-if="showRequiredClientField(client.cif)" class="invalid-feedback mb-0">
                  <small>Campo obligatorio</small>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="client-contact-person" class="">Persona de contacto</label>
                <el-input id="client-contact-person" v-model="client.contactPersonName" class="search-input"
                  :color="'light'" :placeholder="'Ingrese el contacto'" />
              </div>
            </div>
          </div>
          <div class="col-md-4 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="client-phone" class="">Teléfono</label>
                <el-input id="client-phone" v-model="client.phone" class="search-input" type="number"
                  :rules="{ min: 9, max: 13 }" :color="'light'" :placeholder="'Ingrese el número'" />
              </div>
            </div>
          </div>
          <div class="col-md-4 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="client-email" class="">Email</label>
                <el-input id="client-email" v-model="client.email" class="search-input" :rules="{ required: true }"
                  :color="'light'" :placeholder="'Ingrese el email'" />
                <p v-if="showRequiredClientField(client.email)" class="invalid-feedback mb-0">
                  <small>Campo obligatorio</small>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="client-address" class="">Dirección</label>
                <el-input id="client-address" v-model="client.address" class="search-input" :color="'light'"
                  :placeholder="'Ingrese la dirección'" />
              </div>
            </div>
          </div>
          <div class="col-md-4 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="client-extranet-url" class="">URL de extranet</label>
                <el-input id="client-extranet-url" v-model="client.extranetUrl" class="search-input" :color="'light'"
                  :placeholder="'https://www.ejemplo.com'" />
                <p class="client-extranet-url-hint mb-0">
                  <small>Opcional. Dominio propio de este cliente para los enlaces de sus emails (reset de contraseña, tickets...). Si se deja vacío se usa la extranet genérica.</small>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-2 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="tag" class="">Etiqueta</label>
                <el-input id="tag" v-model="client.tag" class="search-input" :rules="{ max: 5 }" :color="'light'"
                  :maxlength="5" :placeholder="'Ingrese una etiqueta'" @input="onTagInput">
                  <template #suffix>
                    <el-tooltip content="Generar etiqueta automáticamente" placement="top">
                      <font-awesome-icon icon="arrows-rotate" class="pointer tag-regenerate-icon"
                        :class="{ 'tag-regenerate-icon--loading': isSuggestingTag }" @click="regenerateTag" />
                    </el-tooltip>
                  </template>
                </el-input>
                <p v-if="showRequiredClientField(client.tag)" class="invalid-feedback mb-0">
                  <small>Campo obligatorio</small>
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-2 d-flex flex-column justify-content-start">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="hourlyRate" class="">Precio/Hora</label>
                <el-input-number id="hourlyRate" v-model="client.hourlyRate" class="search-input" :min="0" :max="100"
                  :controls="false" style="width: 100%;" :step="1" :color="'light'"
                  :placeholder="'Ingrese el precio hora'" />
              </div>

            </div>
          </div>
          <div class="client-save-actions col-md-4 d-flex justify-content-center align-items-center mt-5 ms-auto">
            <button class="btn btn_darkpink search w-50 button-save-client" @click="onSave">
              {{ isCreateMode ? 'CREAR' : 'GUARDAR' }}
            </button>
          </div>
        </div>
        <div class="row client-secondary-options-row">
          <div class="col-md-2 d-flex flex-column justify-content-start" v-if="!isCreateMode">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="client.id" class="">Fuente</label>
                <el-select v-model="client.source" class="search-input w-100" color="light"
                  placeholder="Elija la fuente" :disabled="isSourceDisabled">
                  <el-option v-for="item in optionsSource" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>
            </div>
          </div>
          <div class="col-md-2 d-flex flex-column justify-content-start" v-if="!isCreateMode">
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="internalCustomerAction" class="">Cliente interno</label>
                <el-switch id="internalCustomerAction" v-model="client.internalCustomer" class="ml-4" />
              </div>
            </div>
          </div>
          <div class="col-md-4 d-flex flex-column justify-content-start" v-else>
            <div class="d-flex flex-row">
              <div class="d-flex flex-nowrap flex-column mt-4 ml-4 w-100 label-common">
                <label for="internalCustomer" class="">Cliente interno</label>
                <el-switch id="internalCustomer" v-model="client.internalCustomer" class="ml-4" />
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </el-row>
    <el-row v-if="!isCreateMode" class="client-users-header">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center w-100 my-2">
        <div class="d-flex flex-row justify-content-start align-items-center">
          <img src="@/assets/icons/pc-icon.svg" class=" mx-2" width="30" height="30" alt="name-icon" />

          <div class="title-text ml-4">
            Usuarios Extranet
          </div>
        </div>
        <div class="client-users-header__actions">
          <button class="btn btn_darkpink search client-users-new-btn" @click="openDialog">
            NUEVO USUARIO
          </button>
        </div>
      </div>
    </el-row>
    <el-row v-if="!isCreateMode" class="client-users-row">
      <div class="table table-users client-users-table table-not-mobile">
        <div v-if="paginatedExtranetUsers && paginatedExtranetUsers.length > 0">
          <table>
            <colgroup>
              <col class="client-users-table__email-column" />
              <col class="client-users-table__name-column" />
              <col class="client-users-table__role-column" />
              <col class="client-users-table__actions-column" />
            </colgroup>
            <tbody>
              <tr v-for="user_extranet in paginatedExtranetUsers" :key="user_extranet.id">
                <td>
                  <div class="client-user-cell">
                    <img src="@/assets/icons/envelop-icon.svg" class="icon-email" width="25" height="25"
                      alt="email-icon" />
                    {{ user_extranet.email }}
                  </div>
                </td>
                <td>
                  <div class="client-user-cell">
                    <img src="@/assets/icons/user.svg" class="icon-user" width="25" height="25" alt="user-icon" />
                    {{ user_extranet.name }} {{ user_extranet.lastName }}
                  </div>
                </td>
                <td>
                  <div class="client-user-cell client-user-cell--role">
                    <svg version="1.1" class="icon-user" xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 595.3 841.9"
                      style="enable-background: new 0 0 595.3 841.9" xml:space="preserve">
                      <path transform="matrix(1,0,0,1,-2,-0.2)" class="st0"
                        d="M 265.6 725.7 c -27.1 0 -52.7 -5.5 -75.8 -16.2 c -23.3 -10.7 -45 -26.7 -64.3 -47.5 c -19.1 -20.5 -33.8 -44.1 -43.5 -69.6 c -9.7 -25.6 -14.5 -53.4 -14.5 -82.9 V 208.5 h 154.4 v 301.1 c 0.4 13.9 4.6 25.1 12.8 34 c 8.4 8.9 18.5 13.5 30.9 13.7 c 12.8 -0.5 23.1 -4.8 31.5 -13.9 c 8.4 -8.9 12.6 -20.1 12.8 -34 l 0 -210.7 l 154.4 -96.8 v 307.7 c 0 29.4 -4.8 57.3 -14.5 82.9 c -9.7 25.6 -24.2 49.1 -43.5 69.6 c -19.1 20.8 -40.8 36.7 -64.3 47.5 C 318.7 720.2 292.9 725.4 265.6 725.7 z M 84.3 226.7 v 282.8 c 0 27.2 4.4 52.7 13.2 76 c 8.8 23.3 22.1 44.5 39.7 63.5 c 17.9 19.2 37.8 33.8 59 43.6 c 21.2 9.8 44.5 14.6 69.3 14.6 c 25.2 0 48.7 -5 70.2 -14.6 c 21.4 -9.8 41.2 -24.4 58.8 -43.6 c 17.6 -18.9 30.9 -40.2 39.7 -63.5 c 8.8 -23.3 13.2 -48.8 13.2 -76 v -276.2 l -120.8 75.8 v 200.4 c -0.4 18.9 -6.5 34.5 -17.9 46.8 c -11.6 12.3 -26 18.7 -43.1 18.9 l -0.2 0 l -0.2 0 c -16.8 -0.5 -31.1 -6.8 -42.6 -18.9 c -11.3 -12.3 -17.4 -27.8 -17.9 -46.6 l 0 -0.2 V 226.7 H 84.3 z" />
                    </svg>
                    <div class="client-user-role">
                      {{ showRoles(user_extranet.roles) }}
                    </div>
                  </div>
                </td>
                <td>
                  <div class="client-user-actions">
                    <font-awesome-layers class="fa-2x pointer" title="Dar de baja"
                      @click="desactiveUsuario(user_extranet)">
                      <font-awesome-icon icon="circle" style="color:red" />
                      <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                    <shared-button-plus title="Editar usuario" @onClick="editUser(user_extranet)" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>
          <h5>No hay usuarios extranet disponibles...</h5>
        </div>
      </div>
      <div class="table-mobile client-users-mobile w-100">
        <div v-if="paginatedExtranetUsers && paginatedExtranetUsers.length > 0">
          <el-row v-for="user_extranet in paginatedExtranetUsers" :key="user_extranet.id" class="mt-3">
            <el-card class="client-user-card w-100 pointer" @click="editUser(user_extranet)">
              <div class="client-user-card__header">
                <div class="client-user-card__content">
                  <div class="client-user-card__item">
                    <div class="client-user-cell">
                      <img src="@/assets/icons/envelop-icon.svg" class="icon-email" width="25" height="25"
                        alt="email-icon" />
                      <div>{{ user_extranet.email }}</div>
                    </div>
                  </div>
                  <div class="client-user-card__item">
                    <div class="client-user-cell">
                      <img src="@/assets/icons/user.svg" class="icon-user" width="25" height="25" alt="user-icon" />
                      <div>{{ user_extranet.name }} {{ user_extranet.lastName }}</div>
                    </div>
                  </div>
                </div>
                <div class="client-user-card__actions client-user-card__actions--mobile">
                  <font-awesome-layers class="fa-2x pointer" title="Dar de baja"
                    @click.stop="desactiveUsuario(user_extranet)">
                    <font-awesome-icon icon="circle" style="color:red" />
                    <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                  </font-awesome-layers>
                </div>
              </div>
              <div class="client-user-card__item">
                <div class="client-user-cell client-user-cell--role">
                  <svg version="1.1" class="icon-user" xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 595.3 841.9"
                    style="enable-background: new 0 0 595.3 841.9" xml:space="preserve">
                    <path transform="matrix(1,0,0,1,-2,-0.2)" class="st0"
                      d="M 265.6 725.7 c -27.1 0 -52.7 -5.5 -75.8 -16.2 c -23.3 -10.7 -45 -26.7 -64.3 -47.5 c -19.1 -20.5 -33.8 -44.1 -43.5 -69.6 c -9.7 -25.6 -14.5 -53.4 -14.5 -82.9 V 208.5 h 154.4 v 301.1 c 0.4 13.9 4.6 25.1 12.8 34 c 8.4 8.9 18.5 13.5 30.9 13.7 c 12.8 -0.5 23.1 -4.8 31.5 -13.9 c 8.4 -8.9 12.6 -20.1 12.8 -34 l 0 -210.7 l 154.4 -96.8 v 307.7 c 0 29.4 -4.8 57.3 -14.5 82.9 c -9.7 25.6 -24.2 49.1 -43.5 69.6 c -19.1 20.8 -40.8 36.7 -64.3 47.5 C 318.7 720.2 292.9 725.4 265.6 725.7 z M 84.3 226.7 v 282.8 c 0 27.2 4.4 52.7 13.2 76 c 8.8 23.3 22.1 44.5 39.7 63.5 c 17.9 19.2 37.8 33.8 59 43.6 c 21.2 9.8 44.5 14.6 69.3 14.6 c 25.2 0 48.7 -5 70.2 -14.6 c 21.4 -9.8 41.2 -24.4 58.8 -43.6 c 17.6 -18.9 30.9 -40.2 39.7 -63.5 c 8.8 -23.3 13.2 -48.8 13.2 -76 v -276.2 l -120.8 75.8 v 200.4 c -0.4 18.9 -6.5 34.5 -17.9 46.8 c -11.6 12.3 -26 18.7 -43.1 18.9 l -0.2 0 l -0.2 0 c -16.8 -0.5 -31.1 -6.8 -42.6 -18.9 c -11.3 -12.3 -17.4 -27.8 -17.9 -46.6 l 0 -0.2 V 226.7 H 84.3 z" />
                  </svg>
                  <div class="client-user-role">
                    {{ showRoles(user_extranet.roles) }}
                  </div>
                </div>
              </div>
            </el-card>
          </el-row>
        </div>
        <div v-else class="mt-3">
          <h5>No hay usuarios extranet disponibles...</h5>
        </div>
      </div>
      <SharedElementPagination v-model:current-page="extranetUsersPage" v-model:page-size="extranetUsersItemsPerPage"
        size="default" :page-size-options="pageSizeOptions" layout="prev, pager, next, sizes"
        :total="extranetUsers.length" class="tracker-pagination client-table-pagination" show-always
        @size-change="onChangeExtranetUsersItemsPerPage" />
    </el-row>
    <el-row v-if="!isCreateMode" class="client-projects-header">
      <div
        class="client-projects-header__content d-flex flex-column flex-md-row justify-content-between align-items-md-center w-100 my-2">
        <div class="client-projects-header__main d-flex flex-column flex-md-row align-items-md-center">
          <div class="d-flex flex-row justify-content-start align-items-center">
            <img src="@/assets/icons/pc-icon.svg" class=" mx-2" width="30" height="30" alt="name-icon" />

            <div class="title-text ml-4">
              Proyectos
            </div>
          </div>
          <div class="filter-options">
            <template v-for="(item, index) in projectFilters" :key="item.value">
              <span class="option" :class="{ active: item.active }" @click="changeProjectFilter(item.value)">
                {{ item.name }}
              </span>
              <span v-if="index < projectFilters.length - 1" class="separator">|</span>
            </template>
          </div>
        </div>
        <div class="client-projects-header__actions">
          <button class="btn btn_darkpink search client-projects-new-btn" @click="addProjectToClient">
            AÑADIR PROYECTO
          </button>
        </div>
      </div>
    </el-row>
    <el-row v-if="!isCreateMode" class="client-projects-row">
      <div class="table table-users client-projects-table table-not-mobile">
        <div v-if="paginatedClientProjects && paginatedClientProjects.length > 0">
          <table>
            <colgroup>
              <col class="client-projects-table__name-column" />
              <col class="client-projects-table__archived-column" />
            </colgroup>
            <tbody>
              <tr v-for="project in paginatedClientProjects" :key="project.id || project['@id']" class="pointer"
                @click="goToProject(project)">
                <td>
                  <span class="client-project-name">
                    {{ project.name }}
                  </span>
                </td>
                <td>
                  <span :class="['project-archived-badge', { 'project-archived-badge--active': project.archived }]">
                    {{ formatArchived(project.archived) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>
          <h5>No se han encontrado proyectos según el filtro seleccionado...</h5>
        </div>
      </div>
      <div class="table-mobile client-projects-mobile w-100">
        <div v-if="paginatedClientProjects && paginatedClientProjects.length > 0">
          <el-row v-for="project in paginatedClientProjects" :key="project.id || project['@id']" class="mt-3">
            <el-card class="client-project-card w-100 pointer" @click="goToProject(project)">
              <div class="client-project-card__header">
                <span class="client-project-card__title">
                  {{ project.name }}
                </span>
              </div>
              <div class="client-project-card__item client-project-card__item--archived">
                <span class="client-project-card__label">Archivado:</span>
                <span :class="['project-archived-badge', { 'project-archived-badge--active': project.archived }]">
                  {{ formatArchived(project.archived) }}
                </span>
              </div>
            </el-card>
          </el-row>
        </div>
        <div v-else class="mt-3">
          <h5>No se han encontrado proyectos según el filtro seleccionado...</h5>
        </div>
      </div>
      <SharedElementPagination v-model:current-page="projectsPage" v-model:page-size="projectsItemsPerPage"
        size="default" :page-size-options="pageSizeOptions" layout="prev, pager, next, sizes"
        :total="clientProjects.length" class="tracker-pagination client-table-pagination" show-always
        @size-change="onChangeProjectsItemsPerPage" />
    </el-row>
    <el-row v-if="isCreateMode" class="d-flex flex-row justify-content-end el-col-xs-24">
      <div class="d-flex flex-row justify-content-end  mt-lg-5 w-75 el-col-xs-24 mb-2 mt-3">
        <button class="btn btn_yellow search w-25 mr-5 btn-client-users" @click="goBackToClients()">
          VOLVER
        </button>
      </div>
    </el-row>
    <el-dialog v-if="!isCreateMode" v-model="dialogVisible" :show-close="false"
      :style="{ borderRadius: `var(--el-border-radius-round)` }" class="dialog-edit-client">
      <template #header>
        <div class="d-flex flex-row justify-content-start align-items-center my-2">
          <svg id="icon-search" class="search-icon" width="44" height="37" viewBox="0 0 44 37" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1.15269 30.0573H20.8415V34.6802H11.5851C10.8865 34.6802 10.4324 35.1448 10.4324 35.8301C10.4324 36.527 10.8981 36.98 11.5851 36.98H32.4266C33.1252 36.98 33.5793 36.5154 33.5793 35.8301C33.5793 35.1332 33.1135 34.6802 32.4266 34.6802H23.1585V30.0573H42.8473C43.5459 30.0573 44 29.5927 44 28.9074V1.16994C44 0.473018 43.5343 0.0200195 42.8473 0.0200195H1.15269C0.465732 0.0200195 0 0.484633 0 1.16994V28.8958C0 29.5927 0.465732 30.0573 1.15269 30.0573ZM2.31702 2.33147H41.683V27.7458H2.31702V2.33147Z"
              fill="#474CE6" />
            <path
              d="M38.2133 4.64294H5.78672C5.08812 4.64294 4.63403 5.10756 4.63403 5.79286V24.2729C4.63403 24.9698 5.09977 25.4228 5.78672 25.4228H38.2133C38.9119 25.4228 39.366 24.9582 39.366 24.2729V5.79286C39.366 5.10756 38.9119 4.64294 38.2133 4.64294ZM37.049 23.123H6.95105V6.9544H37.0606V23.123H37.049Z"
              fill="#474CE6" />
          </svg>

          <div class="title-text ml-4 mr-auto">
            Nuevo usuario
          </div>
          <svg class="close-icon pointer" width="724" height="1024" viewBox="0 0 724 1024"
            xmlns="http://www.w3.org/2000/svg" @click="dialogVisible = false"
            style="width: 30px;  transform: translateY(-10px);">
            <defs />
            <path
              d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
          </svg>
        </div>
      </template>

      <div class="container-fluid">
        <div class="row">
          <div class="col-md-6">
            <label for="nuser">Nombre usuario</label>
            <el-input id="nuser" v-model="user.name" :rules="{ required: true }" :color="'light'"
              :placeholder="'Ingrese el nombre'" />
          </div>
          <div class="col-md-6">
            <label for="lastname">Apellido</label>
            <el-input id="lastname" v-model="user.lastName" class="search-input" :rules="{ required: true }"
              :color="'light'" :placeholder="'Ingrese los apellidos'" />
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-md-12">
            <label for="email">Email</label>
            <el-input id="email" v-model="user.email" :rules="{ required: true }" :color="'light'"
              :placeholder="'Ingrese el nombre'" />
          </div>
        </div>
        <div class="row row-cols-1 mt-2">
          <div class="col-md-12">
            <label for="role" class="">Role</label>
            <el-select v-model="user.roles" size="small" value-key="roleName">
              <el-option v-for="item in optionsRoles" :key="item.roleName" :label="item.name" :value="item" />
            </el-select>
          </div>
        </div>
      </div>
      <el-row>
        <div class="d-flex justify-content-end align-items-center mt-5">
          <button class="btn btn_yellow search  mr-5" @click="dialogVisible = false; resetUser()">
            CANCELAR
          </button>
          <button class="btn btn_darkpink search" @click="saveUser">
            GUARDAR
          </button>
        </div>
      </el-row>
    </el-dialog>
    <SharedDeleteConfirmDialog v-model="deactivateUserDialogShown" title="Eliminar usuario"
      message="Esta seguro que desea continuar?" :loading="isDeactivatingUser" @confirm="confirmDeactivateUser"
      @cancel="resetDeactivateUser" />
  </div>
</template>

<style lang="scss">
$--el-border-radius-round: 10%;

.close-icon {
  width: 6%;
  height: 6%;
  fill: $bg-blue;

}

.card-search {
  border-radius: 45px;
  background: url('../../assets/icons/bg-edit-cliente.png'), white;
  background-repeat: no-repeat;
  background-position-x: 100%;
  padding: 15px;
  background-size: 48%;
  overflow: visible !important;
}

#icon-search {
  fill: $bg-blue;
}

.client-page-title-row {
  min-width: 0;
}

.client-page-title-icon {
  width: 44px;
  height: auto;
  flex: 0 0 auto;
}

.client-page-title {
  flex: 0 0 auto;
  text-align: left;
  line-height: 1.05;
  letter-spacing: 0;
  white-space: nowrap;
  overflow-wrap: normal;
  word-break: normal;
}

.client-projects-header {
  margin-top: 24px;
}

.client-users-header {
  margin-top: 16px;
}

.client-page-return-btn {
  margin-right: 16px;
}

.client-save-actions {
  gap: 16px;
}

.tag-regenerate-icon {
  color: $bg-blue;
  cursor: pointer;
}

.client-extranet-url-hint {
  color: #6c757d;
}

.tag-regenerate-icon--loading {
  animation: tag-regenerate-spin 1s linear infinite;
}

@keyframes tag-regenerate-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.client-users-header__actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.client-users-new-btn {
  width: 220px;
  height: 2.5em;
  font-weight: bolder;
}

.client-projects-header__main {
  min-width: 0;
}

.client-projects-header__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.client-projects-new-btn {
  width: 220px;
  height: 2.5em;
  font-weight: bolder;
}

.client-table-pagination {
  justify-content: flex-end;
  width: 100%;
  margin-top: 12px;
}

.client-users-row {
  width: 100%;
}

.client-users-table {
  width: 100%;
}

.client-users-table table {
  width: 100%;
  table-layout: fixed;
}

.client-users-table td {
  vertical-align: middle;
}

.client-users-table__email-column,
.client-users-table__name-column {
  width: 30%;
}

.client-users-table__role-column {
  width: 25%;
}

.client-users-table__actions-column {
  width: 15%;
}

.icon-email {
  fill: $bg-blue;
  width: 1.6em;
  min-width: 1.6em;
}

.icon-user {
  fill: $bg-blue;
  width: 1.4em;
  min-width: 1.4em;
}

.client-user-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.client-user-cell--role {
  color: $bg-blue;
}

.client-user-email,
.client-user-name {
  color: $bg-blue;
  font-weight: 800;
}

.client-user-role {
  color: $bg-blue;
  font-weight: 700;
}

.client-user-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.client-projects-header .filter-options {
  font-weight: bold;
  font-size: 32px;
  margin-left: 10px;
}

.client-projects-header .option {
  cursor: pointer;
  color: #3B47FF;
  margin: 0 0.2em;
  font-size: 15px;
}

.client-projects-header .option.active {
  color: #FF006E;
}

.client-projects-header .separator {
  color: #3B47FF;
  margin: 0 0.2em;
  font-size: 15px;
}

.client-projects-row {
  margin-top: -12px;
  width: 100%;
}

.client-projects-table {
  width: 100%;
}

.client-projects-table table {
  width: 100%;
  table-layout: fixed;
}

.client-projects-table td {
  vertical-align: middle;
}

.client-projects-table__name-column {
  width: 85%;
}

.client-projects-table__archived-column {
  width: 15%;
}

.client-project-name {
  color: $bg-blue;
  font-weight: 800;
}

.project-archived-badge {
  display: inline-block;
  min-width: 48px;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background-color: #27ae60;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.3;
  text-align: center;
}

.project-archived-badge--active {
  background-color: #E53935;
}

.table {
  padding-top: 0px;
}

.table-mobile {
  display: none;
}

.client-user-card,
.client-project-card {
  border-radius: 18px;
}

.client-user-card__item,
.client-project-card__item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 10px;
  word-break: break-word;
}

.client-project-card__label {
  color: $bg-blue;
  font-weight: 800;
}

.client-project-card__item--archived {
  align-items: center;
  flex-direction: row;
  gap: 8px;
}

.client-user-card__actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.client-user-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.client-user-card__content {
  flex: 1;
  min-width: 0;
}

.client-user-card__actions--mobile {
  display: none;
  flex-shrink: 0;
  margin-top: 0;
}

.client-project-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-top: 12px;
  margin-bottom: 5px;
}

.client-project-card__title {
  color: $bg-blue;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}

@media (max-width: 767px) {
  #icon-search {
    width: 30px;
  }

  .client-page-title-row {
    width: 100%;
  }

  .client-page-title {
    font-size: 1.8rem !important;
    margin-left: 10px !important;
  }

  .card-search {
    background: white;
    min-height: 0;
  }

  .dialog-edit-client {
    min-width: 75%;
  }

  .button-save-client {
    margin-top: 10px;
    width: 100% !important;
  }

  .client-save-actions {
    flex-direction: column;
    align-items: stretch !important;
    gap: 8px;
  }

  .btn-client-users {
    width: 50% !important;
  }

  .client-users-header__actions {
    width: 100%;
    margin-top: 12px;
    flex-direction: column;
    align-items: stretch;
  }

  .client-projects-header__actions {
    width: 100%;
    margin-top: 12px;
    flex-direction: column;
    align-items: stretch;
  }

  .client-page-return-btn {
    margin-right: 8px;
  }

  .client-users-header__actions .client-users-new-btn {
    width: 100% !important;
  }

  .client-projects-header__actions .client-projects-new-btn {
    width: 100% !important;
  }

  .client-table-pagination {
    justify-content: flex-start;
  }

  .client-user-cell {
    align-items: flex-start;
  }

  .client-user-card__header {
    margin-top: 10px;
  }

  .client-user-card__header .client-user-card__item {
    margin-bottom: 8px;
  }

  .client-user-card__actions {
    display: none;
  }

  .client-user-card__actions--mobile {
    display: flex;
  }

  .client-user-card__edit-action {
    display: none;
  }

  .client-projects-header .filter-options {
    font-size: 20px;
    margin-top: 10px;
  }

  .client-projects-header .title-text {
    font-size: 2.2em;
  }

  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    display: block;
  }
}
</style>
